import type { Account } from '@api/hooks/useAccounts';
import { toastSuccess } from '@components/Toast/Toast';
import { Chip, CopyIcon } from '@gurupay/gp-ui-library';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getAmountLabel } from '../utils';

interface AccountInfoMobileProps {
	account: Account;
}

const statusLabelKeys: Record<string, string> = {
	CLOSED: 'accounts.filters.status.closed',
	BLOCKED: 'accounts.filters.status.blocked',
	CREDIT_BLOCKED: 'accounts.filters.status.creditBlocked',
};

export const AccountInfoMobile = ({ account }: AccountInfoMobileProps) => {
	const { t } = useTranslation();
	const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		navigator?.clipboard?.writeText(account.iban);
		toastSuccess(t('common.successCopyToClipboard'));
	};

	return (
		<>
			<div className="mb-space-16">
				<div className="text-sm-bold text-neutral-950 flex flex-row gap-space-4 items-center">
					{account.iban}
					<button
						aria-label={t('common.copyToClipboard', { iban: account.iban })}
						className="bg-transparent border-0 p-0"
						onClick={handleCopy}
						type="button"
					>
						<CopyIcon
							aria-hidden="true"
							className="w-space-16 text-neutral-600 hover:text-neutral-800 cursor-pointer transition-all duration-300 ease-in-out"
						/>
					</button>
				</div>
				<div className="text-sm-medium text-neutral-600">
					{account.ownerName}
					{account.label ? ` | ${account.label}` : ''}
				</div>
				{!!statusLabelKeys[account.status] && (
					<Chip
						className="mt-space-4 badge"
						color={account.status === 'CLOSED' ? 'error' : 'warning'}
						label={t(statusLabelKeys[account.status])}
					/>
				)}
			</div>
			<div className="flex flex-row gap-space-4 justify-between items-end">
				<dl>
					<dt className="text-sm-medium text-neutral-600">Available:</dt>
					<dd className="text-sm-bold">
						{getAmountLabel(account.availableBalance, account.status)}
					</dd>
				</dl>
				<div className="text-sm-bold">{account.currency}</div>
			</div>
		</>
	);
};
